/**
 * Odia alphabet charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_indic_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const odiaAlphabetChartsPack: ToilePack = {
    slug: 'odia-alphabet-charts',
    category: 'classroom',
    name: 'Odia alphabet charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'full', label: 'Full alphabet', subject: 'an Odia alphabet chart of vowels and consonants in a grid'},
        {key: 'pictures', label: 'Picture chart', subject: 'an Odia alphabet wall chart with pictures'},
        {key: 'barakhadi', label: 'Barakhadi', subject: 'an Odia barakhadi grid for one consonant'},
    ],
    styles: [
        {key: 'classic', label: 'Classic ruled', style: 'a clean school chart with thin ruled black lines and bold black lettering', palette: 'black ink on white paper with one accent colour for the heading', framing: 'the chart filling the page inside an even margin'},
        {key: 'rainbow', label: 'Rainbow classroom', style: 'a cheerful classroom wall chart with each row on a different bright colour band and rounded friendly lettering', palette: 'saturated red, orange, yellow, green, blue and purple bands', framing: 'the chart filling the page inside a bold coloured border and a margin'},
        {key: 'pastel', label: 'Soft pastel', style: 'a gentle nursery chart of soft rounded tiles with large lettering and a small doodle border', palette: 'soft pastel pink, mint, butter yellow and sky blue on cream', framing: 'centered on the page with a wide margin'},
    ],
    directions: ['large print', 'black and white', 'with border'],
    directionHint: 'try: large print',
    seo: {
        title: 'Odia Alphabet Charts: Full Chart, Pictures, Barakhadi',
        metaDescription: 'Nine printable Odia alphabet pages: a full chart, a picture chart and a barakhadi grid, in ruled, rainbow and pastel styles.',
        h1: 'Odia alphabet charts: full chart, picture chart, barakhadi',
        tagline: 'Nine pages in three styles, each style with a full chart, a picture chart and a barakhadi grid.',
        intro: 'Parents and teachers in Odisha print the Odia alphabet, the barnamala, for children learning to read. These nine pages give a full chart of vowels and consonants, a picture chart with pictures beside the letters, and a barakhadi grid for one consonant. Each comes as a ruled classroom sheet, a rainbow chart or a soft pastel nursery chart. Type a twist, such as large print or a border, and the same layouts carry it.',
        useCases: [
            'a classroom wall',
            'a study table at home',
            'a nursery wall',
            'a daily practice sheet',
        ],
        faq: [
            {q: 'Is the lettering in Odia?', a: 'Yes. The chart letters are Odia script. Only the page heading is in English.'},
            {q: 'What is the barakhadi page?', a: 'It is a grid for one consonant with its vowel signs, so the child reads each syllable form in turn.'},
            {q: 'What is the difference between the three styles?', a: 'Classic ruled uses thin black lines and one accent colour. Rainbow classroom gives each row its own bright band inside a bold border. Soft pastel uses pink, mint, butter yellow and sky blue tiles on cream.'},
            {q: 'Can I change the chart or ask for a twist?', a: 'Yes. Type the change, such as large print, black and white or a border. Your request shapes the result; it is not a promise of what these nine pages show.'},
        ],
    },
}
